import { useState } from 'react';

import type { Task, TaskStatus } from '../api.js';
import { formatDate } from '../labels.js';
import { MOVE_LABELS, PRIORITY_LABELS, STATUS_LABELS } from './task-labels.js';

/**
 * The board and the calendar — two more arrangements of the SAME rows.
 *
 * Same fetch, same permissions, same `allowed_next` buttons as the list. The
 * board says WHY a task sits in Blocked, on the card; the calendar names
 * undated work out loud instead of hiding it, because work with no due date
 * never appears on a calendar and that is exactly how it gets forgotten.
 */
const BOARD_COLUMNS: readonly { key: TaskStatus; label: string }[] = [
  { key: 'todo', label: 'To do' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'done', label: 'Done' },
];

export function TaskBoard({
  tasks,
  busy,
  onMove,
}: {
  readonly tasks: readonly Task[];
  readonly busy: boolean;
  readonly onMove: (taskId: string, to: TaskStatus) => void;
}): JSX.Element {
  return (
    <div className="task-board">
      {BOARD_COLUMNS.map((column) => {
        const cards = tasks.filter((t) => t.status === column.key);
        return (
          <section key={column.key} className="board-column" aria-label={column.label}>
            <h4 className="board-head">
              {column.label} <span className="tree-code">{cards.length}</span>
            </h4>
            {cards.length === 0 ? (
              <p className="hint">None</p>
            ) : (
              <ul className="board-cards">
                {cards.map((task) => (
                  <li key={task.id} className={`board-card task-${task.status}`}>
                    <span className="task-title">{task.title}</span>
                    <span className="task-meta">
                      <span className={`chip chip-priority-${task.priority}`}>
                        {PRIORITY_LABELS[task.priority]}
                      </span>
                      {task.owner_name && <span>{task.owner_name}</span>}
                      {task.due_date && (
                        <span className={task.overdue ? 'task-overdue' : undefined}>
                          {task.overdue ? 'Overdue' : formatDate(task.due_date)}
                        </span>
                      )}
                    </span>
                    {/* Why it sits in this column, on the card itself. */}
                    {task.blockers[0] && (
                      <span className="snag-note">{task.blockers[0].description}</span>
                    )}
                    <span className="row-actions">
                      {task.allowed_next.map((to) => (
                        <button
                          key={to}
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => onMove(task.id, to)}
                        >
                          {MOVE_LABELS[to]}
                          <span className="visually-hidden"> {task.title}</span>
                        </button>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The calendar — what lands when
// ---------------------------------------------------------------------------

export function TaskCalendar({ tasks }: { readonly tasks: readonly Task[] }): JSX.Element {
  const now = new Date();
  const [month, setMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  );

  const [yearString, monthString] = month.split('-') as [string, string];
  const year = Number(yearString);
  const monthIndex = Number(monthString) - 1;
  const first = new Date(Date.UTC(year, monthIndex, 1));
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

  const byDay = new Map<string, Task[]>();
  for (const task of tasks) {
    if (!task.due_date) continue;
    const list = byDay.get(task.due_date) ?? [];
    list.push(task);
    byDay.set(task.due_date, list);
  }
  const undated = tasks.filter((t) => !t.due_date);

  const shift = (delta: number): void => {
    const moved = new Date(Date.UTC(year, monthIndex + delta, 1));
    setMonth(`${moved.getUTCFullYear()}-${String(moved.getUTCMonth() + 1).padStart(2, '0')}`);
  };

  const monthName = first.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <div className="task-calendar">
      <div className="section-head">
        <h4 className="sub-head">{monthName}</h4>
        <span className="row-actions">
          <button type="button" className="link-button" onClick={() => shift(-1)}>
            Previous month
          </button>
          <button type="button" className="link-button" onClick={() => shift(1)}>
            Next month
          </button>
        </span>
      </div>

      <ol className="calendar-grid">
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1;
          const iso = `${yearString}-${monthString}-${String(day).padStart(2, '0')}`;
          const due = byDay.get(iso) ?? [];
          return (
            <li key={iso} className={`calendar-day${due.length > 0 ? ' has-tasks' : ''}`}>
              <span className="calendar-date">{day}</span>
              {due.map((task) => (
                <span
                  key={task.id}
                  className={`calendar-task${task.overdue ? ' task-overdue' : ''}`}
                >
                  {task.title}
                  {/* Never colour alone: the state travels in words. */}
                  <span className="visually-hidden">
                    {' '}
                    — {STATUS_LABELS[task.status]}
                    {task.overdue ? ', overdue' : ''}
                  </span>
                </span>
              ))}
            </li>
          );
        })}
      </ol>

      {undated.length > 0 && (
        <p className="hint">
          <strong>No date:</strong> {undated.map((t) => t.title).join(', ')} — work without a due
          date never appears on a calendar, which is exactly how it gets forgotten.
        </p>
      )}
    </div>
  );
}
